import { creatorSubscriptionsIds } from "@data/bloggersLanding";
import { appMounted, routePathNameChanged } from "@features/app/model";
import { IFlagsmithContract } from "@features/flagsmith/model";
import { parseQueryParams } from "@features/helpers";
import { IPaymentContract } from "@features/payment/ui";
import { landingBeginClicked, userQueryCollected } from "@features/shared/events";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { IRouterContract } from "@features/shared/router";
import { createEffect, createEvent, createStore, sample } from "effector";
import { interval } from "patronum";

const saveQueryParamsFx = createEffect(() => {
    if (typeof window !== "undefined") {
        const query = window.location.search;
        if (query) {
            sessionStorage.setItem("query_params", query);

            const parsed = parseQueryParams(query);
            userQueryCollected(parsed);
        }
    }
});

const calculateFutureSubscribers = (currentSubs: number, days: number, platform: number): number => {
    const coefficients = [0.01, 0.015, 0.007];
    const K = coefficients[platform] ?? coefficients[0];

    const S = 1000000;

    if (days <= 30) {
        let subs = currentSubs;
        for (let day = 0; day < days; day++) {
            const growth = K * (1 - subs / S);
            subs += subs * growth;
        }
        return Math.round(subs);
    }

    const S0 = currentSubs;
    const exponent = Math.exp(-K * days * 0.85);

    return Math.round(S / (1 + (S / S0 - 1) * exponent));
};

const changesubscribersCount = createEvent<number>();
const daysCountCount = createEvent<number>();
const selectSocial = createEvent<number>();
export const changeEmailValueOnCreator = createEvent<string>();
const startRegistration = createEvent();
const startSubscribersCounter = createEvent();
const stopSubscribersCounter = createEvent();
const startDaysCounter = createEvent();
const stopDaysCounter = createEvent();

const { tick: tick1 } = interval({
    timeout: 10,
    start: startSubscribersCounter,
    stop: stopSubscribersCounter
});

const { tick: tick2 } = interval({
    timeout: 15,
    start: startDaysCounter,
    stop: stopDaysCounter
});

const $subscribersCount = createStore(0).on(tick1, (s) => s + 1000);
const $daysCount = createStore(0).on(tick2, (s) => s + 1);
const $totalSubscribers = createStore(0);
const $halfPeriodSubscribers = createStore(0);
const $currentSocial = createStore(0);
const $email = createStore("").on(changeEmailValueOnCreator, (_, p) => p);

const trackLandingBeginClickedFx = createEffect((source: string) => {
    landingBeginClicked(source);
});

sample({
    clock: $subscribersCount,
    filter: (count) => count >= 50000,
    target: stopSubscribersCounter
});

sample({
    clock: $daysCount,
    filter: (count) => count >= 28,
    target: stopDaysCounter
});

sample({
    clock: changesubscribersCount,
    target: $subscribersCount
});

sample({
    clock: daysCountCount,
    target: $daysCount
});

sample({
    clock: selectSocial,
    target: $currentSocial
});

sample({
    clock: [$daysCount, $subscribersCount, $currentSocial, appMounted],
    source: {
        days: $daysCount,
        currentSubs: $subscribersCount,
        platform: $currentSocial
    },
    fn: ({ days, currentSubs, platform }) => calculateFutureSubscribers(currentSubs, days, platform),
    target: $totalSubscribers
});

sample({
    clock: [$daysCount, $subscribersCount, $currentSocial, appMounted],
    source: {
        days: $daysCount,
        currentSubs: $subscribersCount,
        platform: $currentSocial
    },
    fn: ({ days, currentSubs, platform }) => calculateFutureSubscribers(currentSubs, days / 2, platform),
    target: $halfPeriodSubscribers
});

const contract = new ModelContract(
    { $subscribersCount, $daysCount, $totalSubscribers, $currentSocial, $halfPeriodSubscribers, $email },
    {
        changesubscribersCount,
        daysCountCount,
        selectSocial,
        changeEmailValueOnCreator,
        startRegistration,
        startSubscribersCounter,
        startDaysCounter
    },
    { trackLandingBeginClickedFx },
    {}
);

export type ICreatorLandingContarct = typeof contract;

export class CreatorLandingModule extends ModuleDescription<ICreatorLandingContarct> {
    constructor(
        public paymentModule: IPaymentContract,
        public flagsModule: IFlagsmithContract,
        public routerModule: IRouterContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run(): void {
        sample({
            clock: routePathNameChanged,
            filter: () => {
                if (typeof window === "undefined") return false;
                return window.location.pathname === "/creator";
            },
            target: saveQueryParamsFx
        });

        sample({
            clock: this.flagsModule.stores.$identificationCompleted,
            filter: () => {
                if (typeof window === "undefined") return false;
                return window.location.pathname === "/creator" || window.location.pathname === "/creator/paywall";
            },
            target: this.paymentModule.events.getSolidgatePlans
        });

        sample({
            clock: this.paymentModule.stores.$solidgatePlans,
            filter: () => {
                if (typeof window === "undefined") return false;

                return window.location.pathname === "/creator" || window.location.pathname === "/creator/paywall";
            },
            fn: (plans) => {
                return creatorSubscriptionsIds
                    .map((id) => plans.find((plan) => plan.id === id))
                    .filter((plan) => plan !== undefined)
                    .sort((a, b) => a.prices[0].product_price - b.prices[0].product_price);
            },
            target: this.paymentModule.stores.$filteredSolidgatePlans
        });

        sample({
            clock: startRegistration,
            filter: () => typeof window !== "undefined" && window.location.pathname === "/creator",
            fn: () => `/creator/set-email${window.location.search}`,
            target: this.routerModule.events.routerPush
        });
    }
}
